import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { OrangeTextButton } from "../OrangeTextButton";
import { ActuateButton } from "../ActuateButton";
import {
  ImportSettingKey,
  ImportSettingsApplyResponse,
  ImportSettingsPreview,
  countImportPreview,
  summarizeImportResults,
} from "../../utils/importSettingsApi";

export const IMPORT_SETTINGS_DIALOG_TITLE = "Import settings from";

export const ZONES_CAVEAT =
  "These are motion exclusion areas, a starting point. Review them on each camera's page.";

type RowKey = "zones" | "names" | "enabled";

interface ImportRow {
  key: RowKey;
  /** Setting keys this row sends; masks ride with zones. */
  settings: ImportSettingKey[];
  label: string;
  caveat?: string;
  /** Nothing to import: the row renders disabled and unchecked. */
  empty: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Builds the per-group rows from the preview. Exported for the tests; the
 * dialog only shows groups the site's About payload offers.
 */
export const buildImportRows = (
  preview: ImportSettingsPreview,
  offered: string[],
  integrationLabel: string
): ImportRow[] => {
  const counts = countImportPreview(preview);
  const offer = new Set(offered);
  const rows: ImportRow[] = [];
  const total = plural(counts.total, "camera");

  if (offer.has("zones") || offer.has("masks")) {
    const settings: ImportSettingKey[] = [];
    if (offer.has("zones")) settings.push("zones");
    if (offer.has("masks")) settings.push("masks");
    const masksNote =
      offer.has("masks") && counts.masks > 0
        ? ` (includes ${plural(counts.masks, "privacy mask")})`
        : "";
    rows.push({
      key: "zones",
      settings,
      label: `Ignore zones from motion exclusion areas: ${counts.zones} of ${total}${masksNote}`,
      caveat: ZONES_CAVEAT,
      empty: counts.zones === 0 && counts.masks === 0,
    });
  }
  if (offer.has("names")) {
    rows.push({
      key: "names",
      settings: ["names"],
      label: `Camera names: ${counts.namesDiffer} of ${total} differ from the current name`,
      empty: counts.namesDiffer === 0,
    });
  }
  if (offer.has("enabled")) {
    rows.push({
      key: "enabled",
      settings: ["enabled"],
      label: `Enabled state: ${counts.disabled} of ${total} ${
        counts.disabled === 1 ? "is" : "are"
      } disabled in ${integrationLabel}`,
      empty: counts.disabled === 0,
    });
  }
  return rows;
};

/**
 * The post-auto-add "import from the VMS" card (ENG-2461). Presentational:
 * the caller owns the preview/apply calls (see ImportSettingsOnboardingPrompt)
 * and advances the wizard from `onSkip` / `onContinue`.
 */
export const ImportSettingsDialog = ({
  open,
  preview,
  offeredSettings,
  integrationLabel,
  applying,
  results,
  applyError,
  onSkip,
  onImport,
  onContinue,
}: {
  open: boolean;
  preview: ImportSettingsPreview;
  offeredSettings: string[];
  integrationLabel: string;
  applying: boolean;
  results: ImportSettingsApplyResponse | null;
  applyError: string | null;
  onSkip: () => void;
  onImport: (settings: ImportSettingKey[]) => void;
  onContinue: () => void;
}) => {
  const rows = React.useMemo(
    () => buildImportRows(preview, offeredSettings, integrationLabel),
    [preview, offeredSettings, integrationLabel]
  );
  // Default on for every row that has something to import.
  const [checked, setChecked] = React.useState<Record<RowKey, boolean>>(() => ({
    zones: rows.some((r) => r.key === "zones" && !r.empty),
    names: rows.some((r) => r.key === "names" && !r.empty),
    enabled: rows.some((r) => r.key === "enabled" && !r.empty),
  }));

  const selectedSettings = React.useMemo(() => {
    const out: ImportSettingKey[] = [];
    rows.forEach((row) => {
      if (checked[row.key] && !row.empty) out.push(...row.settings);
    });
    return out;
  }, [rows, checked]);

  const label = integrationLabel || "the VMS";
  const finished = results !== null || applyError !== null;
  const summary = results ? summarizeImportResults(results.results) : null;

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : finished ? onContinue : onSkip}
      aria-labelledby="import-settings-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id="import-settings-title"
        sx={{ fontFamily: "Mulish", color: "#001943", fontWeight: "bold" }}
      >
        {IMPORT_SETTINGS_DIALOG_TITLE} {label}?
      </DialogTitle>
      <DialogContent sx={{ fontFamily: "Mulish" }}>
        {!finished && (
          <div className="font-mulish text-actuate-blue">
            <p className="mb-[8px] text-[14px]">
              {label} already has configuration for these cameras. Choose what
              to bring over. Settings a camera already has here are left alone.
            </p>
            {rows.map((row) => (
              <div
                key={row.key}
                className="mb-[6px]"
                data-testid={`import-row-${row.key}`}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked[row.key] && !row.empty}
                      disabled={row.empty || applying}
                      onChange={(event) => {
                        const next = event.target.checked;
                        setChecked((prev) => ({ ...prev, [row.key]: next }));
                      }}
                      sx={{
                        color: "#FF8400",
                        "&.Mui-checked": { color: "#FF8400" },
                      }}
                      inputProps={{ "aria-label": row.label }}
                    />
                  }
                  label={row.label}
                  sx={{
                    "& .MuiFormControlLabel-label": { fontFamily: "Mulish" },
                  }}
                />
                {row.caveat && !row.empty && (
                  <p className="pl-[32px] text-[12px] text-gray-600">
                    {row.caveat}
                  </p>
                )}
              </div>
            ))}
            {applying && (
              <div className="flex items-center gap-3 pt-2">
                <CircularProgress color="warning" size={20} />
                <p className="text-[14px]">Importing from {label}…</p>
              </div>
            )}
          </div>
        )}

        {applyError && <Alert severity="warning">{applyError}</Alert>}

        {summary && (
          <Alert
            severity={summary.error > 0 ? "warning" : "success"}
            data-testid="import-settings-summary"
          >
            <p>
              Imported: {summary.imported} · Already had a value:{" "}
              {summary.skipped_has_value} · Nothing on {label}:{" "}
              {summary.no_source_value} · Errors: {summary.error}
            </p>
            {summary.errors.length > 0 && (
              <ul className="list-disc pl-5 pt-1">
                {summary.errors.slice(0, 5).map((row) => (
                  <li key={`${row.camera_id}-${row.setting}`}>
                    Camera {row.camera_id} ({row.setting})
                    {row.message ? `: ${row.message}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {!finished && (
          <>
            <OrangeTextButton
              onClick={onSkip}
              text="Skip"
              disabled={applying}
            />
            <ActuateButton
              onClick={() => onImport(selectedSettings)}
              text="Import selected"
              disabled={applying || selectedSettings.length === 0}
            />
          </>
        )}
        {finished && (
          <ActuateButton
            onClick={onContinue}
            text="Continue"
            disabled={false}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
